import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Store, Save } from 'lucide-react';
import { createRetailer, fetchCountriesForDropdown } from '../services/api.ts';
import './AddRetailer.css';

interface Country {
  id: number;
  name: string;
  code: string;
}

const AddRetailer: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    logo: '',
    domain: '',
    enable: true,
    country_id: 0
  });

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountries(data);
        if (data.length > 0) {
          setFormData((prev) => ({ ...prev, country_id: data[0].id }));
        }
      } catch (err) {
        console.error('Error loading countries:', err);
      }
    };
    loadCountries();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError(null);
      setLoading(true);
      await createRetailer({
        name: formData.name,
        short_name: formData.short_name,
        logo: formData.logo,
        domain: formData.domain,
        enable: formData.enable,
        country_id: formData.country_id
      });
      navigate('/dashboard/retailers');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to create retailer';
      setError(errorMessage);
      console.error('Create error:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-retailer-page">
      <div className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/dashboard/retailers')}>
            <ArrowLeft size={20} />
          </button>
          <div className="header-title">
            <h2>Add New Retailer</h2>
            <p className="subtitle">Create a new retailer for your platform</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>Error: {error}</span>
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-card">
            <div className="card-header">
              <Store size={20} />
              <h3>Retailer Information</h3>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Country <span className="required">*</span></label>
                <select
                  value={formData.country_id}
                  onChange={(e) => setFormData({ ...formData, country_id: parseInt(e.target.value) })}
                  required
                >
                  <option value={0} disabled>Select a country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Retailer Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter retailer name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Short Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.short_name}
                  onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                  placeholder="Enter short name (e.g., COLES)"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Logo URL <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  placeholder="https://example.com/logo.png"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Domain</label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="e.g. walmart.com"
                />
                <p className="field-hint">The retailer's website domain (used for product search)</p>
              </div>

              {formData.logo && (
                <div className="form-group full-width">
                  <label>Logo Preview</label>
                  <div className="logo-preview">
                    <img 
                      src={formData.logo} 
                      alt="Logo preview" 
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Status</label>
                <div className="toggle-group">
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={formData.enable}
                      onChange={(e) => setFormData({ ...formData, enable: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`status-text ${formData.enable ? 'active' : 'inactive'}`}>
                    {formData.enable ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={() => navigate('/dashboard/retailers')}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Creating...' : 'Create Retailer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRetailer;
